import { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import NumberField from './NumberField';
import ResultPanel from './ResultPanel';
import ToolLayout from './ToolLayout';
import { formatNumber, parseNumber } from '../utils/numbers';
import {
  isentropicSpecificPowerKwPerM3Min,
  rateEfficiency,
  specificPowerFromMeasured,
  toBarGauge,
  toKw,
  toM3Min,
} from '../utils/specificPower';

const powerUnits = [
  { value: 'kw', label: 'Kilowatts (kW)' },
  { value: 'hp', label: 'Horsepower (hp)' },
  { value: 'w', label: 'Watts (W)' },
];

const flowUnits = [
  { value: 'm3min', label: 'm³/min' },
  { value: 'm3h', label: 'm³/h' },
  { value: 'lpm', label: 'L/min' },
  { value: 'cfm', label: 'CFM' },
];

const pressureUnits = [
  { value: 'bar', label: 'bar (g)' },
  { value: 'psi', label: 'psi (g)' },
  { value: 'kpa', label: 'kPa (g)' },
];

function SpecificPower() {
  const [power, setPower] = useState('');
  const [powerUnit, setPowerUnit] = useState('kw');
  const [flow, setFlow] = useState('');
  const [flowUnit, setFlowUnit] = useState('m3min');
  const [pressure, setPressure] = useState('7');
  const [pressureUnit, setPressureUnit] = useState('bar');
  const [stages, setStages] = useState(1);

  const { result, error } = useMemo(() => {
    const powerNum = parseNumber(power);
    const flowNum = parseNumber(flow);
    const pressureNum = parseNumber(pressure);

    if (powerNum == null || flowNum == null) {
      return { result: null, error: '' };
    }

    try {
      if (powerNum < 0) throw new Error('Power cannot be negative');
      if (flowNum <= 0) throw new Error('FAD must be greater than zero');

      const powerKw = toKw(powerNum, powerUnit);
      const fadM3Min = toM3Min(flowNum, flowUnit);
      const measured = specificPowerFromMeasured(powerKw, fadM3Min);
      if (!measured) throw new Error('Could not calculate specific power');

      let theoretical = null;
      let rating = null;
      if (pressureNum != null && pressureNum > 0) {
        const barG = toBarGauge(pressureNum, pressureUnit);
        theoretical = isentropicSpecificPowerKwPerM3Min(barG, 1.01325, Number(stages));
        rating = rateEfficiency(measured.kwPerM3Min, theoretical);
      }

      return { result: { measured, theoretical, rating, powerKw, fadM3Min }, error: '' };
    } catch (err) {
      return { result: null, error: err.message };
    }
  }, [power, powerUnit, flow, flowUnit, pressure, pressureUnit, stages]);

  return (
    <ToolLayout title="Specific Power">
      <Card>
        <CardContent>
          <Typography color="text.secondary" paragraph>
            Specific power is package input power divided by free air delivery. Use nameplate or measured kW and FAD to compare compressors.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={7}>
              <NumberField label="Input power" value={power} onChange={setPower} min={0} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <FormControl>
                <InputLabel>Power unit</InputLabel>
                <Select value={powerUnit} label="Power unit" onChange={(event) => setPowerUnit(event.target.value)}>
                  {powerUnits.map((unit) => (
                    <MenuItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={7}>
              <NumberField
                label="Free air delivery (FAD)"
                value={flow}
                onChange={setFlow}
                min={0}
                helperText="Use FAD, not compressed volume flow"
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <FormControl>
                <InputLabel>FAD unit</InputLabel>
                <Select value={flowUnit} label="FAD unit" onChange={(event) => setFlowUnit(event.target.value)}>
                  {flowUnits.map((unit) => (
                    <MenuItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={7}>
              <NumberField
                label="Discharge pressure (optional)"
                value={pressure}
                onChange={setPressure}
                min={0}
                helperText="Used for theoretical comparison"
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <FormControl>
                <InputLabel>Pressure unit</InputLabel>
                <Select
                  value={pressureUnit}
                  label="Pressure unit"
                  onChange={(event) => setPressureUnit(event.target.value)}
                >
                  {pressureUnits.map((unit) => (
                    <MenuItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl>
                <InputLabel>Compression stages</InputLabel>
                <Select value={stages} label="Compression stages" onChange={(event) => setStages(event.target.value)}>
                  <MenuItem value={1}>Single stage</MenuItem>
                  <MenuItem value={2}>Two stage (equal ratio, intercooled)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {error && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          {result && (
            <Box sx={{ mt: 2 }}>
              {result.rating && (
                <Chip
                  label={`${result.rating.label} · ${formatNumber(result.rating.efficiency, 0)}% isentropic`}
                  color={result.rating.color}
                  sx={{ mb: 2 }}
                />
              )}
              <ResultPanel
                title="Specific power"
                items={[
                  {
                    label: 'kW / (m³/min)',
                    value: formatNumber(result.measured.kwPerM3Min, 2),
                    fullWidth: true,
                  },
                  {
                    label: 'kW / 100 CFM',
                    value: formatNumber(result.measured.kwPer100Cfm, 2),
                  },
                  {
                    label: 'kWh / m³',
                    value: formatNumber(result.measured.kwhPerM3, 4),
                  },
                ]}
              />
              {result.theoretical != null && (
                <ResultPanel
                  title="Theoretical comparison"
                  items={[
                    {
                      label: 'Isentropic minimum (kW / m³/min)',
                      value: formatNumber(result.theoretical, 2),
                    },
                    {
                      label: 'Your FAD (m³/min)',
                      value: formatNumber(result.fadM3Min, 2),
                    },
                  ]}
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary" paragraph>
        Specific power = input power / FAD. CAGI and ISO 1217 datasheets usually quote kW/100 CFM or kW/(m³/min) at a stated pressure.
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        The optional comparison uses single-stage (or equal-ratio two-stage) isentropic compression from 1.013 bar(a). Real oil-injected rotary screws at 7 bar(g) are often around 5.5–7.5 kW/(m³/min).
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Use package electrical input where possible. Motor nameplate kW without efficiency will understate specific power.
      </Typography>
    </ToolLayout>
  );
}

export default SpecificPower;
